import React from 'react';

import { createDocumentRoute, isUUIDV4 } from '@aline/common';
import { CircularProgress, Typography } from '@mui/material';
import clsx from 'clsx';
import { CirclePlusIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Apollo } from 'src/gql/generated';
import { useMaybeDocumentParams } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import type { RedlineDocumentContext } from './redline-types';
import { createAIChatItem } from '../../../helpers/aiChatItem';

const useStyles = makeStyles()(theme => ({
  actionButton: {
    '&:hover': {
      backgroundColor: '#F0F0F0',
    },
    alignItems: 'center',
    borderRadius: 16,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1.25),
  },
  actionButtonIcon: {
    height: 18,
    width: 18,
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  headerActions: {
    alignItems: 'center',
    display: 'flex',
    marginLeft: 'auto',
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  redlineListBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
  },
  redlineListContainer: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2, -1),
    position: 'relative',
  },
  redlineListHeader: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    display: 'flex',
    padding: theme.spacing(0.5, 1),
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  title: {
    marginLeft: theme.spacing(0.5),
  },
}));

interface RedlineListProps {
  /**
   * The child action items to render.
   */
  children: React.ReactNode;
  /**
   * The document context (documentId + a groupId to anchor on) for the header
   * and "view in doc" navigation. Derived from the first edit, insert, or
   * removal; a note-only list supplies just the documentId (null groupId, so
   * no navigation). Null until at least one item has been parsed.
   */
  firstRedlineContext: RedlineDocumentContext | null;
  /**
   * Whether the message is currently streaming.
   */
  isLoading: boolean;
  /**
   * The message ID this redline list belongs to.
   */
  messageId: string;
  /**
   * The thread ID this redline list belongs to.
   */
  threadId: string;
}

/**
 * Container for a list of redline action items.
 *
 * Edits are applied to the document on the server over collab/Yjs, so this
 * component is purely presentational — it shows a header, a "view in doc"
 * button when the doc is not currently open, and the streamed action items.
 */
export const RedlineList = React.memo(function RedlineList({
  children,
  firstRedlineContext,
  isLoading,
  messageId,
  threadId,
}: RedlineListProps) {
  const { classes } = useStyles();
  const { documentId: urlDocumentId } = useMaybeDocumentParams();

  /**
   * The redline's documentId comes straight off the parsed model stream, so it
   * can be empty or an invented placeholder when no document was attached.
   * `GetSuggestionDocument` types it as `UUID!`, which rejects a non-UUID at
   * variable coercion and surfaces a raw GraphQL error on the page, so only
   * query once the id is a real UUID.
   */
  const redlineDocumentId = firstRedlineContext?.documentId;
  const queryableDocumentId =
    redlineDocumentId && isUUIDV4(redlineDocumentId) ? redlineDocumentId : null;

  const { data } = Apollo.useGetSuggestionDocumentQuery({
    skip: !queryableDocumentId,
    variables: { documentId: queryableDocumentId ?? '' },
  });

  /**
   * `urlDocumentId` is undefined outside a document route, and so is the
   * redline's id when the model never anchored one. Require a real id on both
   * sides, otherwise two absent values compare equal and every card claims to
   * be the current document.
   */
  const isCurrentDocument = Boolean(urlDocumentId) && redlineDocumentId === urlDocumentId;
  const displayDocumentName = isCurrentDocument
    ? 'Current Document'
    : data?.document?.name || 'Untitled Document';

  return (
    <section className={classes.redlineListContainer}>
      <div className={classes.redlineListHeader}>
        {isLoading && (
          <div className={classes.loadingContainer}>
            <CircularProgress size={18} />
          </div>
        )}
        <Typography noWrap className={classes.title} variant="subtitle1">
          {isLoading ? 'Generating edits...' : displayDocumentName}
        </Typography>
        <div className={classes.headerActions}>
          <RedlineListActionButton
            firstRedlineContext={firstRedlineContext}
            messageId={messageId}
            threadId={threadId}
          />
        </div>
      </div>
      <div className={classes.redlineListBody}>{children}</div>
    </section>
  );
});

interface RedlineListActionButtonProps {
  firstRedlineContext: RedlineDocumentContext | null;
  messageId: string;
  threadId: string;
}

/**
 * Shows "View edits in doc" when the redlined document is not the active one;
 * nothing when it is (the toolbar inside the editor handles in-doc actions).
 * A null groupId means a note-only list — nothing landed in the document, so
 * there's nothing to navigate to.
 */
function RedlineListActionButton({
  firstRedlineContext,
  messageId,
  threadId,
}: RedlineListActionButtonProps) {
  const { documentId: urlDocumentId } = useMaybeDocumentParams();
  const docNotInView = !urlDocumentId || urlDocumentId !== firstRedlineContext?.documentId;

  if (!docNotInView || !firstRedlineContext || !firstRedlineContext.groupId) return null;

  return (
    <OpenRedlineListDocumentButton
      documentId={firstRedlineContext.documentId}
      groupId={firstRedlineContext.groupId}
      messageId={messageId}
      threadId={threadId}
    />
  );
}

function OpenRedlineListDocumentButton({
  documentId,
  groupId,
  messageId,
  threadId,
}: {
  documentId: string;
  groupId: string;
  messageId: string;
  threadId: string;
}) {
  const { classes } = useStyles();
  const navigate = useNavigate();

  return (
    <div
      className={clsx(classes.actionButton)}
      onClick={() => {
        const documentRoute = createDocumentRoute(documentId, {
          aiChatItem: createAIChatItem({
            groupId,
            messageId,
            threadId,
            type: 'suggestion',
          }),
        });
        navigate(documentRoute);
      }}
    >
      <CirclePlusIcon className={classes.actionButtonIcon} strokeWidth={1.5} />
      <Typography className={classes.actionButtonText}>View edits in doc</Typography>
    </div>
  );
}
